import { useCallback, useEffect, useMemo, useState } from 'react'
import { sileo } from 'sileo'
import { useApp } from '../context/AppContext'
import slaService from '../services/slaService'
import ticketService from '../services/ticketService'
import {
  SLA_POLICIES,
  getSlaPolicies,
  policiesFromRows,
  setSlaPolicies,
} from '../utils/sla'
import {
  applySuggestions,
  draftFromPolicies,
  rowsFromDraft,
  suggestSlaPolicies,
  validateSlaDraft,
} from '../utils/slaTuning'
import { TICKET_TYPE_LIST } from '../utils/ticketType'
import { employeeName } from '../utils/employee'

const TYPES = TICKET_TYPE_LIST.map((t) => t.value)

/**
 * Estado de Configuración → Tiempos de SLA: matriz editable (en horas),
 * autocálculo con el histórico, restablecer y guardar.
 *
 * @param {number[]} priorityIds - Prioridades (columnas de la matriz).
 * @returns {Object}
 */
export default function useSlaSettings(priorityIds) {
  const { employee } = useApp()
  const [saved, setSaved] = useState(null)
  const [draft, setDraft] = useState(null)
  const [meta, setMeta] = useState(null)
  const [suggestions, setSuggestions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const pidsKey = priorityIds.join(',')

  useEffect(() => {
    if (!priorityIds.length) return
    let active = true
    slaService.getAll().then(({ success, data, error }) => {
      if (!active) return
      if (!success)
        sileo.error({ title: 'Error cargando el SLA', description: error })
      const rows = success ? data : []
      const policies = policiesFromRows(rows)
      const initial = draftFromPolicies(policies, TYPES, priorityIds)
      setSaved(initial)
      setDraft(initial)
      const last = [...rows].sort((a, b) =>
        String(b.updatedAt).localeCompare(String(a.updatedAt))
      )[0]
      setMeta(last ? { at: last.updatedAt, by: last.updatedBy } : null)
      setLoading(false)
    })
    return () => {
      active = false
    }
    // La lista de prioridades se compara por contenido, no por referencia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pidsKey])

  const errors = useMemo(() => (draft ? validateSlaDraft(draft) : {}), [draft])
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved]
  )

  const setCell = useCallback((type, pid, field, value) => {
    setDraft((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        cells: {
          ...prev[type].cells,
          [pid]: { ...prev[type].cells[pid], [field]: value },
        },
      },
    }))
  }, [])

  const toggleType = useCallback((type) => {
    setDraft((prev) => ({
      ...prev,
      [type]: { ...prev[type], enabled: !prev[type].enabled },
    }))
  }, [])

  /** Calcula sugerencias con el histórico y las aplica al borrador. */
  const autoCalculate = useCallback(async () => {
    setCalculating(true)
    const { success, data, error } = await ticketService.getSlaHistory()
    setCalculating(false)
    if (!success) {
      sileo.error({ title: 'No se pudo leer el histórico', description: error })
      return
    }
    const result = suggestSlaPolicies(data, {
      types: TYPES,
      priorityIds,
      current: getSlaPolicies(),
    })
    setSuggestions(result)
    setDraft((prev) => applySuggestions(prev, result))
    sileo.info({
      title: 'Tiempos sugeridos aplicados',
      description: 'Revise los valores y guarde para aplicarlos.',
    })
  }, [priorityIds])

  /** Vuelve a los valores por defecto del sistema (sin guardar). */
  const resetToDefaults = useCallback(() => {
    setSuggestions(null)
    setDraft(draftFromPolicies(SLA_POLICIES, TYPES, priorityIds))
  }, [priorityIds])

  const discard = useCallback(() => {
    setSuggestions(null)
    setDraft(saved)
  }, [saved])

  const save = useCallback(async () => {
    if (Object.keys(errors).length) return
    setSaving(true)
    const rows = rowsFromDraft(draft)
    const by = employeeName(employee) || 'Usuario'
    const { success, error } = await slaService.saveAll(rows, by)
    setSaving(false)
    if (!success) {
      sileo.error({ title: 'No se pudo guardar el SLA', description: error })
      return
    }
    // Vigente de inmediato para los tickets que se creen desde ahora.
    setSlaPolicies(policiesFromRows(rows))
    setSaved(draft)
    setSuggestions(null)
    setMeta({ at: new Date().toISOString(), by })
    sileo.success({ title: 'Tiempos de SLA guardados' })
  }, [draft, errors, employee])

  return {
    draft,
    errors,
    dirty,
    meta,
    suggestions,
    loading,
    saving,
    calculating,
    setCell,
    toggleType,
    autoCalculate,
    resetToDefaults,
    discard,
    save,
  }
}
